import { customFetchV2 } from "./fetch";

export interface DnsDelegateSendResponse {
  lastSentEmail: string;
  lastSentAt: number;
}

export interface DnsDelegateStatusResponse {
  lastSentEmail?: string;
  lastSentAt?: number;
  hasActiveToken: boolean;
  deliveryFailed?: boolean;
  deliveryStatus?: string;
  deliveryError?: string;
}

export interface DnsDelegateSessionResponse {
  domain: string;
  adminServiceUrl: string;
  charityName?: string;
}

export class DnsDelegateApiError extends Error {
  status: number;
  code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

async function delegateFetch<T>(url: string, init: RequestInit): Promise<T> {
  const response = await fetch(url, { ...init, mode: "cors" });

  if (!response.ok) {
    let message = `HTTP ${response.status}: ${response.statusText}`;
    let code: string | undefined;
    try {
      const body = await response.json();
      message = body.message || message;
      code = body.code;
    } catch {
      // keep default message
    }
    throw new DnsDelegateApiError(message, response.status, code);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
}

export async function sendDnsDelegateEmail(
  adminServiceUrl: string,
  charityId: string,
  firebaseToken: string,
  recipientEmail: string
): Promise<DnsDelegateSendResponse> {
  const fullUrl = `${adminServiceUrl}/api/v1/${charityId}/email-setup/dns-delegate-email?token=${encodeURIComponent(
    firebaseToken
  )}`;

  return customFetchV2({
    url: fullUrl,
    method: "POST",
    mode: "cors",
    body: JSON.stringify({ recipientEmail }),
    headers: {
      "content-type": "application/json",
    },
  });
}

export async function getDnsDelegateStatus(
  adminServiceUrl: string,
  charityId: string,
  firebaseToken: string
): Promise<DnsDelegateStatusResponse> {
  const fullUrl = `${adminServiceUrl}/api/v1/${charityId}/email-setup/dns-delegate-status?token=${encodeURIComponent(
    firebaseToken
  )}`;

  return customFetchV2({
    url: fullUrl,
    method: "GET",
    mode: "cors",
    headers: {
      "content-type": "application/json",
    },
  });
}

export async function getDnsDelegateSession(
  adminServiceUrl: string,
  delegateToken: string
): Promise<DnsDelegateSessionResponse> {
  const fullUrl = `${adminServiceUrl}/api/v1/email-setup/dns-delegate/session?token=${encodeURIComponent(
    delegateToken
  )}`;

  return delegateFetch(fullUrl, {
    method: "GET",
    headers: {
      "content-type": "application/json",
    },
  });
}

export async function getDnsDelegateDomain(
  adminServiceUrl: string,
  delegateToken: string
) {
  const fullUrl = `${adminServiceUrl}/api/v1/email-setup/dns-delegate/email-domain?token=${encodeURIComponent(
    delegateToken
  )}`;

  return delegateFetch(fullUrl, {
    method: "GET",
    headers: {
      "content-type": "application/json",
    },
  });
}

export async function getDnsDelegateEntriToken(
  adminServiceUrl: string,
  delegateToken: string
): Promise<{ authToken: string; applicationId: string }> {
  const fullUrl = `${adminServiceUrl}/api/v1/email-setup/dns-delegate/entri/token?token=${encodeURIComponent(
    delegateToken
  )}`;

  return delegateFetch(fullUrl, {
    method: "GET",
    headers: {
      "content-type": "application/json",
    },
  });
}

export async function validateDnsDelegateDomain(
  adminServiceUrl: string,
  delegateToken: string
): Promise<{ valid: boolean; message?: string }> {
  const fullUrl = `${adminServiceUrl}/api/v1/email-setup/dns-delegate/email-domain/validate?token=${encodeURIComponent(
    delegateToken
  )}`;

  return delegateFetch(fullUrl, {
    method: "POST",
    headers: {
      "content-type": "application/json",
    },
    body: JSON.stringify({}),
  });
}

export async function completeDnsDelegateSetup(
  adminServiceUrl: string,
  delegateToken: string
): Promise<void> {
  const fullUrl = `${adminServiceUrl}/api/v1/email-setup/dns-delegate/complete?token=${encodeURIComponent(
    delegateToken
  )}`;

  await delegateFetch(fullUrl, {
    method: "POST",
    headers: {
      "content-type": "application/json",
    },
    body: JSON.stringify({}),
  });
}
